import { useEffect, useRef, useState } from 'react';

import { Field } from 'formik';
import { Trash2 } from 'lucide-react';
import { InputButtons } from '../InputButtons';
import { colors } from '../../../../data/constants';
import {
  formatNumberDisplay,
  parseFormattedNumber,
} from '../../../../utils/utils';

export const Input = ({
  formProps,
  label,
  icon,
  placeholder,
  name,
  searchData = [],
  tip = '',
  hint = '',
  searchComponent,
  inputType = 'input',
  type,
  min = type === 'number' ? 0 : undefined,
  isReadOnly = false,
  isResetClicked,
  setIsResetClicked,
  setInputsRefs = () => {},
  isRequired = false,
  buttons = [],
  depedentValue = '',
  hasDepedentValue = false,
  formatDisplay = false,
  idField,
  onClientSelect = null,
  onClearClient = null,
}) => {
  const [isFocused, setIsFocused] = useState();
  const [isTipShown, setIsTipShown] = useState(false);
  const [isSearchShown, setIsSearchShown] = useState(false);
  const [closedManuallySearch, setClosedManuallySearch] = useState(false);
  const [isLabelShown, setIsLabelShown] = useState();
  const [displayValue, setDisplayValue] = useState('');

  const inputRef = useRef();
  const Icon = icon;
  const Label = label;
  const SearchComponent = searchComponent;

  const shouldFormat = formatDisplay && type === 'number';

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const isBelowMin = value =>
    type === 'number' &&
    min !== undefined &&
    (Number(value) < min || (min >= 0 && String(value).includes('-')));

  const handleChange = e => {
    if (shouldFormat) {
      const inputValue = e.target.value;
      const rawValue = parseFormattedNumber(inputValue);
      if (isBelowMin(rawValue)) return;
      formProps?.setFieldValue(name, rawValue);
      setDisplayValue(inputValue);
    } else {
      if (isBelowMin(e.target.value)) return;
      formProps?.handleChange(e);
    }
  };

  const handleSearch = () => {
    if (tip && isFocused) {
      setIsTipShown(true);
    } else {
      setIsTipShown(false);
    }

    if (formProps?.values?.[name]?.length > 0 || !tip) {
      setIsTipShown(false);
      setIsSearchShown(true);
    } else {
      setIsSearchShown(false);
    }
  };

  const handleEmptyList = async data => {
    if (data?.length < 1) {
      await formProps?.setFieldValue(name, '');
    }
  };

  useEffect(() => {
    if (!formProps?.values?.[name]) {
      setIsLabelShown(false);
    } else {
      setIsLabelShown(true);
    }
    // Add && !isFocused to only apply on blur (when not typing)
    if (shouldFormat) {
      setDisplayValue(formatNumberDisplay(formProps?.values?.[name]));
    }
  }, [shouldFormat, isFocused, formProps?.values, name]);

  useEffect(() => {
    if (isResetClicked && name !== 'search' && name !== 'find_deal') {
      setIsLabelShown(false);
      setIsResetClicked(false);
    }
  }, [isResetClicked]);

  useEffect(() => {
    if (formProps?.values?.[name] !== null) {
      if (!closedManuallySearch && isFocused) {
        handleSearch();
      }

      if (!isFocused) {
        setIsTipShown(false);
      }
    }
  }, [formProps?.values?.[name], isFocused]);

  useEffect(() => {
    if (setInputsRefs) {
      setInputsRefs(prevRefs => {
        return {
          ...prevRefs,
          [name]: {
            ...inputRef,
            setFieldValue: formProps?.setFieldValue,
          },
        };
      });
    }
  }, [setInputsRefs, name, inputRef]);

  return (
    <>
      {label && (
        <Label
          name={name}
          placeholder={placeholder}
          isLabelShown={isLabelShown}
        />
      )}

      {icon && (
        <div>
          <Icon />
        </div>
      )}

      <div className="flex items-center w-full gap-3">
        <div className={`flex items-center flex-1 min-w-0 border-b ${
          formProps?.errors?.[name]
            ? 'border-redColor'
            : 'border-gray-200'
        }`}>
          <Field
            innerRef={inputRef}
            placeholder={placeholder}
            name={name}
            className="block flex-1 min-w-0 text-sm leading-6 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none rounded-none border-none"
            type={shouldFormat ? 'text' : type && type.length > 0 ? type : 'text'}
            as={inputType}
            min={shouldFormat ? undefined : min}
            value={shouldFormat ? displayValue : formProps?.values?.[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            readOnly={isReadOnly}
          />

          {onClearClient && formProps?.values?.[name] && (
            <button
              type="button"
              onClick={onClearClient}
              className="flex-shrink-0 leading-none pr-1"
            >
              <Trash2 size={14} color={colors.arrowColor} />
            </button>
          )}
        </div>

        {isRequired && (
          <div className="absolute top-[30%] right-[2%]">
            <span className="text-sm text-redColor">*</span>
          </div>
        )}

        {tip && isTipShown && (
          <div className="absolute w-60 p-2 top-[44px] border-[1px] b-gray-200 bg-white shadow-md z-[1] md:w-96">
            <span className="block text-xs italic">{tip}</span>
          </div>
        )}

        {buttons && buttons.length > 0 && (
          <InputButtons
            buttons={buttons}
            formProps={formProps}
            inputRef={inputRef}
            name={name}
            idField={idField}
            depedentValue={depedentValue}
            hasDepedentValue={hasDepedentValue}
          />
        )}

        {searchComponent && isSearchShown && (
          <SearchComponent
            list={searchData}
            setIsOpen={setIsSearchShown}
            setClosedManuallySearch={setClosedManuallySearch}
            formProps={formProps}
            inputName={name}
            handleEmptyList={handleEmptyList}
            onClientSelect={onClientSelect}
          />
        )}
      </div>

      {formProps?.errors?.[name] && (
          <div className="absolute bottom-[-18px]">
            <span className="text-[10px] text-redColor">
              {formProps.errors[name]}
            </span>
          </div>
        )}

      {hint && hint.length > 0 && (
        <div>
          <span className="text-[10px] text-gray-600">{hint}</span>
        </div>
      )}
    </>
  );
};
